import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import { BottomNavigation, BottomNavigationAction, Paper } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import LogoutConfirmDialog from "../common/LogoutConfirmDialog";
import { useLogoutConfirmation } from "../../../hooks/useLogoutConfirmation";
import { useAuthStore } from "../../../store/useAuthStore";
import CartIcon from "../cart/CartIcon";

import { navTokens } from "./navTokens";

const NAV_HEIGHT = 56;
const MAX_BROWSER_BOTTOM_INSET = 88;
const KEYBOARD_THRESHOLD = 160;

const readBrowserBottomInset = (): number => {
  const viewport = window.visualViewport;
  if (!viewport) return 0;
  const rawInset =
    Math.max(window.innerHeight, document.documentElement.clientHeight) -
    (viewport.offsetTop + viewport.height);

  if (rawInset <= 0) return 0;
  if (rawInset >= KEYBOARD_THRESHOLD) return 0;
  return Math.min(MAX_BROWSER_BOTTOM_INSET, Math.round(rawInset));
};

export default function MobileNav() {
  const { t: myTranslate } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const {
    isLogoutDialogOpen,
    requestLogout,
    closeLogoutDialog,
    confirmLogout,
  } = useLogoutConfirmation();

  const currentValue = React.useMemo(() => {
    if (location.pathname === "/") return "home";
    if (location.pathname.startsWith("/profile")) return "profile";
    if (location.pathname.startsWith("/cart")) return "cart";
    return false;
  }, [location.pathname]);

  React.useEffect(() => {
    let rafId = 0;
    let lastInset = -1;
    const root = document.documentElement;

    const applyInset = (inset: number) => {
      if (inset === lastInset) return;
      lastInset = inset;
      root.style.setProperty("--mobile-browser-bottom-inset", `${inset}px`);
    };

    const updateInsetVar = () => {
      rafId = 0;
      applyInset(readBrowserBottomInset());
    };

    const scheduleUpdate = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(updateInsetVar);
    };

    const viewport = window.visualViewport;
    root.style.setProperty("--mobile-browser-bottom-inset", "0px");
    scheduleUpdate();
    viewport?.addEventListener("resize", scheduleUpdate);
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("orientationchange", scheduleUpdate);

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      viewport?.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("orientationchange", scheduleUpdate);
      root.style.removeProperty("--mobile-browser-bottom-inset");
    };
  }, []);

  return (
    <Paper
      data-mobile-nav
      elevation={0}
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: "var(--mobile-browser-bottom-inset, 0px)",
        zIndex: 1200,
        borderRadius: 0,
        borderTop: `1px solid ${navTokens.border}`,
        backgroundColor: "rgba(255, 255, 255, 0.98)",
        boxShadow: "0 -10px 28px rgba(15, 23, 42, 0.08)",
        backdropFilter: "saturate(180%) blur(10px)",
        WebkitBackdropFilter: "saturate(180%) blur(10px)",
        pb: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <BottomNavigation
        showLabels
        className="notranslate"
        translate="no"
        value={currentValue}
        sx={{
          height: NAV_HEIGHT,
          backgroundColor: "transparent",
          "& .MuiBottomNavigationAction-root": {
            minWidth: "auto",
            py: "7px",
            color: navTokens.textMuted,
            transition: "color 0.2s ease",
          },
          "& .MuiBottomNavigationAction-root:hover": {
            color: navTokens.primary,
          },
          "& .MuiBottomNavigationAction-label": {
            fontWeight: 600,
          },
          "& .Mui-selected": {
            color: navTokens.primary,
          },
        }}
      >
        <BottomNavigationAction
          className="notranslate"
          translate="no"
          label={myTranslate("navbar.home")}
          icon={<HomeRoundedIcon />}
          value="home"
          onClick={() => navigate("/")}
        />
        <BottomNavigationAction
          className="notranslate"
          translate="no"
          label={myTranslate("navbar.profile")}
          icon={<PersonOutlineRoundedIcon />}
          value="profile"
          onClick={() => navigate("/profile")}
        />
        <BottomNavigationAction
          className="notranslate"
          translate="no"
          label={myTranslate("navbar.cart")}
          icon={<CartIcon size="small" showTooltip={false} variant="icon" />}
          value="cart"
          onClick={() => navigate("/cart")}
        />
        {user && (
          <BottomNavigationAction
            className="notranslate"
            translate="no"
            label={myTranslate("navbar.logout")}
            icon={<LogoutRoundedIcon />}
            value="logout"
            onClick={requestLogout}
          />
        )}
      </BottomNavigation>

      <LogoutConfirmDialog
        open={isLogoutDialogOpen}
        onClose={closeLogoutDialog}
        onConfirm={confirmLogout}
      />
    </Paper>
  );
}
